import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle2, Send, Upload, X, ShieldCheck, FileText } from "lucide-react";

const PLANS = [
  { value: "mensile", label: "Pagamento mensile" },
  { value: "semestrale", label: "Pagamento semestrale" },
  { value: "annuale", label: "Pagamento annuale" },
];

const FilePicker = ({ label, file, onChange, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-midnight font-bold">{label} *</Label>
    <label className="mt-2 flex items-center gap-3 border-2 border-dashed border-border hover:border-gold cursor-pointer p-4 rounded-sm">
      <Upload size={18} className="text-gold" />
      <span className="text-sm text-midnight/70 flex-1 truncate">{file ? file.name : "Scatta una foto o scegli dalla galleria"}</span>
      {file && (
        <button type="button" onClick={(e) => { e.preventDefault(); onChange(null); }} className="text-destructive"><X size={14} /></button>
      )}
      <input
        type="file"
        accept="image/*,application/pdf,.heic,.heif"
        capture="environment"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        data-testid={testId}
      />
    </label>
    <p className="text-[10px] text-midnight/40 mt-1">JPG · PNG · HEIC · PDF · Max 10MB</p>
  </div>
);

export default function Insurance() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", payment_plan: "annuale", notes: "" });
  const [circ, setCirc] = useState(null);
  const [idDoc, setIdDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !circ || !idDoc) {
      toast.error("Compila tutti i campi e carica entrambi i documenti");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("circulation_doc", circ);
      fd.append("id_doc", idDoc);
      await api.post("/insurance", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio richiesta");
    } finally { setLoading(false); }
  };

  if (sent) return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-24 text-center flex-1" data-testid="insurance-success">
        <CheckCircle2 size={60} className="text-gold mx-auto mb-6" />
        <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">Richiesta inviata</h1>
        <p className="text-midnight/70 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
          Grazie {form.name}. Verrai contattato a breve dalla nostra agenzia di assicurazione per finalizzare la polizza.
        </p>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="App min-h-screen flex flex-col" data-testid="insurance-page">
      <Header />
      <section className="max-w-6xl mx-auto px-5 md:px-10 py-16 md:py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="divider-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">Assicurazione Auto</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight heading-gold-silver" data-testid="insurance-hero-title">
              Assicura la tua <br /><span className="italic">auto con noi</span>
            </h1>
            <p className="text-white/80 leading-relaxed mb-8">
              Carica la carta di circolazione e il documento di identità, scegli il piano di pagamento e la nostra agenzia di assicurazione ti contatterà con la miglior proposta.
            </p>
            <div className="card-sharp p-6 surface-silver text-midnight">
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck size={18} className="text-gold" />
                <h3 className="font-display text-lg font-bold text-midnight">Come funziona</h3>
              </div>
              <ol className="space-y-2 text-sm text-midnight font-medium">
                <li className="flex gap-3"><span className="text-gold font-bold">1.</span> Compila il modulo con i tuoi dati</li>
                <li className="flex gap-3"><span className="text-gold font-bold">2.</span> Carica carta di circolazione + documento ID</li>
                <li className="flex gap-3"><span className="text-gold font-bold">3.</span> Scegli il piano di pagamento</li>
                <li className="flex gap-3"><span className="text-gold font-bold">4.</span> Ti contattiamo con la miglior proposta</li>
              </ol>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 card-sharp p-8 md:p-10 space-y-6" data-testid="insurance-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div><Label className="text-xs uppercase tracking-widest text-midnight font-bold">Nome e Cognome *</Label>
                <Input required value={form.name} onChange={update("name")} className="mt-2 rounded-sm" data-testid="ins-name" />
              </div>
              <div><Label className="text-xs uppercase tracking-widest text-midnight font-bold">Telefono *</Label>
                <Input required type="tel" value={form.phone} onChange={update("phone")} className="mt-2 rounded-sm" data-testid="ins-phone" />
              </div>
              <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest text-midnight font-bold">Email *</Label>
                <Input required type="email" value={form.email} onChange={update("email")} className="mt-2 rounded-sm" data-testid="ins-email" />
              </div>
              <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest text-midnight font-bold">Piano di pagamento *</Label>
                <Select value={form.payment_plan} onValueChange={(v) => setForm({ ...form, payment_plan: v })}>
                  <SelectTrigger className="mt-2 rounded-sm select-silver" data-testid="ins-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLANS.map((p) => <SelectItem key={p.value} value={p.value} className="text-midnight font-semibold">{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="pt-4 border-t border-border space-y-4">
              <p className="text-xs uppercase tracking-widest gold-stroke-black">Documenti (foto da telefono/galleria)</p>
              <FilePicker label="Carta di circolazione" file={circ} onChange={setCirc} testId="ins-circ-input" />
              <FilePicker label="Documento di identità" file={idDoc} onChange={setIdDoc} testId="ins-id-input" />
            </div>
            <div className="pt-4 border-t border-border"><Label className="text-xs uppercase tracking-widest text-midnight font-bold">Note</Label>
              <Textarea value={form.notes} onChange={update("notes")} rows={3} className="mt-2 rounded-sm" data-testid="ins-notes" />
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2 py-4 text-sm uppercase tracking-widest font-semibold disabled:opacity-50" data-testid="ins-submit">
              <Send size={16} /> {loading ? "Invio..." : "Invia Richiesta"}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
